import React from 'react';
import AuditLabel from './AuditLabel';
import { calculateSizeTag } from '../utils/dogUtils';
import { formatDateTime } from '../utils/dateUtils';

const STATUS_LABEL = {
    scheduled: 'Programado',
    'in-transit': 'En camino',
    completed: 'Completado',
    cancelled: 'Cancelado'
};

const PAYMENT_LABEL = { pending: 'Pendiente', paid: 'Pagado', package: 'Paquete' };

const TRIP_TYPE_LABEL = {
    'round-trip':   '🔄 Ida y vuelta',
    'pickup-only':  '📍 Solo recogida',
    'dropoff-only': '🏠 Solo entrega',
};

const TransportationCard = ({ booking, onEdit, onDelete, onStatusUpdate, onPaymentUpdate }) => {
    const dogs = booking.dogs && booking.dogs.length > 0 ? booking.dogs : [{
        dog: booking.dog,
        manualDogName: booking.manualDogName,
        manualOwnerName: booking.manualOwnerName,
        manualOwnerPhone: booking.manualOwnerPhone,
        breed: booking.breed,
        weight: booking.weight
    }];

    const dogNames = dogs.map(d => d.dog?.name || d.manualDogName || 'Perro desconocido').join(', ');
    
    // Aggregate owners and phones
    const ownerSet = new Set();
    const phoneSet = new Set();
    dogs.forEach(d => {
        if (d.dog?.owners) {
            d.dog.owners.forEach(o => {
                if (o.name) ownerSet.add(o.name);
                if (o.phone) phoneSet.add(o.phone);
            });
        }
        if (d.manualOwnerName) ownerSet.add(d.manualOwnerName);
        if (d.manualOwnerPhone) phoneSet.add(d.manualOwnerPhone);
    });
    
    const principalOwner = Array.from(ownerSet).join(', ');
    const phone = Array.from(phoneSet).join(', ');

    const tripType = booking.tripType || 'round-trip';
    const showPickup   = tripType !== 'dropoff-only';
    const showDelivery = tripType !== 'pickup-only';

    const canDelete = booking.status === 'scheduled' || booking.status === 'cancelled';

    return (
        <div className={`transportation-card status-${booking.status}`}>
            <div className="tc-header">
                <h3>🚐 {dogNames}</h3>
                <div className="tc-badges">
                    <span className={`tc-badge tc-badge--${booking.status}`}>
                        {STATUS_LABEL[booking.status] || booking.status}
                    </span>
                    <span className={`tc-badge tc-badge--trip-${tripType}`}>
                        {TRIP_TYPE_LABEL[tripType]}
                    </span>
                    <span className={`tc-badge tc-badge--pay-${booking.paymentStatus || 'pending'}`}>
                        {PAYMENT_LABEL[booking.paymentStatus] || 'Pendiente'}
                    </span>
                </div>
            </div>

            <div className="tc-body">
                <div className="tc-dogs-section">
                    {dogs.map((d, i) => {
                        const dWeight = d.dog?.weight || d.weight;
                        const dSizeTag = dWeight ? calculateSizeTag(dWeight) : null;
                        const dBreed = d.dog?.breed || d.breed;
                        return (
                            <div key={i} className="tc-dog-item">
                                <span className="tc-dog-name">🐕 {d.dog?.name || d.manualDogName || 'Perro'}</span>
                                {dBreed && <span className="tc-dog-detail">({dBreed})</span>}
                                {dWeight && (
                                    <span className="tc-dog-detail">
                                        {dWeight} kg 
                                        {dSizeTag && <span className="tc-size-mini">{dSizeTag.tag}</span>}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>

                {principalOwner && (
                    <div className="tc-row">
                        <span className="tc-label">Dueño</span>
                        <span className="tc-value">{principalOwner}</span>
                    </div>
                )}
                {phone && (
                    <div className="tc-row">
                        <span className="tc-label">Teléfono</span>
                        <span className="tc-value">{phone}</span>
                    </div>
                )}

                <div className="tc-divider" />

                {showPickup && booking.pickupTime && (
                    <>
                    <div className="tc-row">
                        <span className="tc-label">📍 Recogida</span>
                        <span className="tc-value">{formatDateTime(booking.pickupTime)}</span>
                    </div>
                    {booking.pickupAddress && (
                        <div className="tc-row tc-row--indent">
                            <span className="tc-label">Dirección</span>
                            <span className="tc-value">{booking.pickupAddress}</span>
                        </div>
                    )}
                    </>
                )}

                {showDelivery && booking.deliveryTime && (
                    <>
                    <div className="tc-row">
                        <span className="tc-label">🏠 Entrega</span>
                        <span className="tc-value">{formatDateTime(booking.deliveryTime)}</span>
                    </div>
                    {booking.deliveryAddress && (
                        <div className="tc-row tc-row--indent">
                            <span className="tc-label">Dirección</span>
                            <span className="tc-value">{booking.deliveryAddress}</span>
                        </div>
                    )}
                    </>
                )}

                {booking.notes && (
                    <div className="tc-notes">
                        <strong>Notas:</strong> {booking.notes}
                    </div>
                )}
            </div>

            <AuditLabel
                createdBy={booking.createdBy}
                updatedBy={booking.updatedBy}
                createdAt={booking.createdAt}
                updatedAt={booking.updatedAt}
                size="small"
            />

            {onStatusUpdate && booking.status === 'scheduled' && (
                <div className="tc-status-actions">
                    <button className="tc-btn tc-btn--transit" onClick={() => onStatusUpdate(booking._id, 'in-transit')}>
                        En camino
                    </button>
                    <button className="tc-btn tc-btn--complete" onClick={() => onStatusUpdate(booking._id, 'completed')}>
                        Completar
                    </button>
                    <button className="tc-btn tc-btn--cancel" onClick={() => onStatusUpdate(booking._id, 'cancelled')}>
                        Cancelar
                    </button>
                </div>
            )}

            {onStatusUpdate && booking.status === 'in-transit' && (
                <div className="tc-status-actions">
                    <button className="tc-btn tc-btn--complete" onClick={() => onStatusUpdate(booking._id, 'completed')}>
                        Completar
                    </button>
                    <button className="tc-btn tc-btn--cancel" onClick={() => onStatusUpdate(booking._id, 'cancelled')}>
                        Cancelar
                    </button>
                </div>
            )}

            {onPaymentUpdate && (
                <div className="tc-payment-actions">
                    {['pending', 'paid', 'package'].map(ps => (
                        <button
                            key={ps}
                            className={`tc-pay-btn tc-pay-btn--${ps}${(booking.paymentStatus || 'pending') === ps ? ' tc-pay-btn--active' : ''}`}
                            onClick={() => onPaymentUpdate(booking._id, ps)}
                            disabled={(booking.paymentStatus || 'pending') === ps}
                        >
                            {PAYMENT_LABEL[ps]}
                        </button>
                    ))}
                </div>
            )}

            <div className="tc-footer-actions">
                {onEdit && (
                    <button className="edit-btn" onClick={() => onEdit(booking)}>
                        Ver / Editar
                    </button>
                )}
                {canDelete && onDelete && (
                    <button className="delete-btn" onClick={() => onDelete(booking._id)}>
                        Eliminar
                    </button>
                )}
            </div>
        </div>
    );
};

export default TransportationCard;
